//  Copyright (c) 2014 Readium Foundation and/or its licensees. All rights reserved.
//
//  Redistribution and use in source and binary forms, with or without modification,
//  are permitted provided that the following conditions are met:
//  1. Redistributions of source code must retain the above copyright notice, this
//  list of conditions and the following disclaimer.
//  2. Redistributions in binary form must reproduce the above copyright notice,
//  this list of conditions and the following disclaimer in the documentation and/or
//  other materials provided with the distribution.
//  3. Neither the name of the organization nor the names of its contributors may be
//  used to endorse or promote products derived from this software without specific
//  prior written permission.

import $ from 'jquery';
import _ from 'underscore';
import MarkupParser from './markup_parser';
import PackageDocument from '../models/package_document';
import SmilDocumentParser from './smil_document_parser';
import Manifest from '../models/manifest';
import { Metadata } from '../models/metadata';
import { SpineItem } from '../models/spine_item';
import { Generator } from '@evidentpoint/readium-cfi-js';
import Semaphore from '../../util/Semaphore';

// `PackageDocumentParser` is used to parse the xml of an epub package
// document and build a javascript object. The constructor accepts an
// instance of `URI` that is used to resolve paths during the process
var PackageDocumentParser = function(publicationFetcher, options) {
  var _packageFetcher = publicationFetcher;
  var _deferredXmlDom = $.Deferred();
  var _xmlDom;

  publicationFetcher.getPackageDom(
    function(packageDom) {
      _xmlDom = packageDom;
      _deferredXmlDom.resolve(packageDom);
    },
    function() {
      _deferredXmlDom.resolve(undefined);
    },
  );

  function fillSmilData(packageDocument, callback) {
    var sema = new Semaphore(1);
    var smilParser = new SmilDocumentParser(packageDocument, publicationFetcher, sema);

    smilParser.fillSmilData(function() {
      // return the parse result
      callback(packageDocument);
    });
  }

  // Parse an XML package document into a javascript object
  this.parse = function(callback) {
    var packageXMLFetch = getPackageXML();
    var encryptionXMLFetch = publicationFetcher.getXmlFileDomPromise('/META-INF/encryption.xml');
    _deferredXmlDom.done(function(xmlDom) {
      if (!xmlDom) {
        callback(undefined);
        return;
      }

      var metadata = getMetadata(xmlDom);

      var page_prog_dir = metadata.page_prog_dir;

      var manifest = new Manifest(getJsonManifest(xmlDom));
      var spine = getJsonSpine(xmlDom, manifest, metadata);

      // try to find a cover image
      var cover = getCoverHref(xmlDom);
      if (cover) {
        metadata.cover_href = cover;
      }
      
      Promise.all(
        [updateMetadataWithIBookProperties(metadata),
          packageXMLFetch.then((containerXmlDom) => {
            var layout = $('Layout', containerXmlDom).text();
            if (layout && layout === 'pre-paginated'){
              metadata.rendition_layout = layout;
            }
          }),
          encryptionXMLFetch]
        ).then(function(values) {
        _packageFetcher.setPackageMetadata(values[2], metadata, function() {
          var packageDocument = new PackageDocument(
            publicationFetcher.getPackageUrl(),
            xmlDom,
            publicationFetcher,
            metadata,
            spine,
            manifest,
          );

          packageDocument.setPageProgressionDirection(page_prog_dir);
          fillSmilData(packageDocument, callback);
        });
      });
    });
  };

  function updateMetadataWithIBookProperties(metadata) {
    return new Promise(function(resolve, reject) {
      if (!metadata.rendition_layout && !options.doNotParseIBooksSpecificXml) {
        var pathToIBooksSpecificXml = '/META-INF/com.apple.ibooks.display-options.xml';
        publicationFetcher.relativeToPackageFetchFileContents(
          pathToIBooksSpecificXml,
          'text',
          function(ibookPropText) {
            if (ibookPropText) {
              var parser = new MarkupParser();
              var propModel = parser.parseXml(ibookPropText);
              var fixLayoutProp = $('option[name=fixed-layout]', propModel)[0];
              if (fixLayoutProp) {
                var fixLayoutVal = $(fixLayoutProp).text();
                if (fixLayoutVal === 'true') {
                  metadata.rendition_layout = 'pre-paginated';
                  console.log('using com.apple.ibooks.display-options.xml fixed-layout property');
                }
              }
            }
            resolve();
          },
          function(err) {
            reject(err);
          },
        );
      } else {
        resolve();
      }
    });
  }

  function getPackageXML() {
    return new Promise(function(resolve, reject) {
      var packageXML = '/META-INF/package.xml';
      publicationFetcher.getXmlFileDom(
        packageXML,
        function(containerXmlDom) {
          resolve(containerXmlDom);
        },
        function(err) {
          reject(err);
        },
      );
    });
  }
  
  function updateMetadataByPackageXML(metadata) {
    return new Promise(function(resolve, reject) {
      var packageXML = '/META-INF/package.xml';
      publicationFetcher.getXmlFileDom(
        packageXML,
        function(containerXmlDom) {
          var layout = $('Layout', containerXmlDom).text();
          if (layout && layout === 'pre-paginated'){
            metadata.rendition_layout = layout;
          }
          resolve();
        },
        function(err) {
          reject(err);
        },
      );
    });
  }

  function getJsonSpine(xmlDom, manifest, metadata) {
    var $spineElements;
    var jsonSpine = [];

    var spine = findXmlElemByLocalNameAnyNS(xmlDom, 'spine');
    ////get Spine Index from XML Dom
    //var spineIndex = Array.prototype.indexOf.call(xmlDom.childNodes[0].childNodes,spine)+1;

    $spineElements = $(spine).children();
    $.each($spineElements, function(spineElementIndex, currSpineElement) {
      var $currSpineElement = $(currSpineElement);
      var idref = $currSpineElement.attr('idref') ? $currSpineElement.attr('idref') : '';
      var manifestItem = manifest.getManifestItemByIdref(idref);

      var id = $currSpineElement.attr('id');
      var viewport;
      _.each(metadata.rendition_viewports, function(vp) {
        if (vp.refines == id) {
          viewport = vp.viewport;
          return true; // break
        }
      });

      const spineItem = new SpineItem({
        rendition_viewport: viewport,
        idref: idref,
        href: manifestItem.href,
        manifest_id: manifestItem.id,
        media_type: manifestItem.media_type,
        media_overlay_id: manifestItem.media_overlay_id,
        linear: $currSpineElement.attr('linear') ? $currSpineElement.attr('linear') : '',
        properties: $currSpineElement.attr('properties')
          ? $currSpineElement.attr('properties')
          : '',
        cfi: Generator.generatePackageDocumentCFIComponentWithSpineIndex(spineElementIndex, xmlDom),
      });

      jsonSpine.push(spineItem);
    });

    return jsonSpine;
  }

  function findXmlElemByLocalNameAnyNS(rootElement, localName, predicate) {
    var elements = rootElement.getElementsByTagNameNS('*', localName);
    if (predicate) {
      return _.find(elements, predicate);
    } else {
      return elements[0];
    }
  }

  function filterXmlElemsByLocalNameAnyNS(rootElement, localName, predicate) {
    var elements = rootElement.getElementsByTagNameNS('*', localName);
    return _.filter(elements, predicate);
  }

  function getElemText(rootElement, localName, predicate) {
    var foundElement = findXmlElemByLocalNameAnyNS(rootElement, localName, predicate);
    if (foundElement) {
      return foundElement.textContent;
    } else {
      return '';
    }
  }

  function getElemAttr(rootElement, localName, attrName, predicate) {
    var foundElement = findXmlElemByLocalNameAnyNS(rootElement, localName, predicate);
    if (foundElement) {
      return foundElement.getAttribute(attrName);
    } else {
      return '';
    }
  }

  function getMetaElemPropertyText(rootElement, attrPropertyValue) {
    var foundElement = findXmlElemByLocalNameAnyNS(rootElement, 'meta', function(element) {
      return element.getAttribute('property') === attrPropertyValue;
    });

    if (foundElement) {
      return foundElement.textContent;
    } else {
      return '';
    }
  }

  function getMetaElemContentByName(rootElement, attrName) {
    var foundElement = findXmlElemByLocalNameAnyNS(rootElement, 'meta', function(element) {
      return element.getAttribute('name') === attrName;
    });

    if (foundElement) {
      return foundElement.getAttribute('content');
    } else {
      return '';
    }
  }

  function getMetadata(xmlDom) {
    var metadata = {};
    var metadataElem = findXmlElemByLocalNameAnyNS(xmlDom, 'metadata');
    var packageElem = findXmlElemByLocalNameAnyNS(xmlDom, 'package');
    var spineElem = findXmlElemByLocalNameAnyNS(xmlDom, 'spine');
    var uniqueIdentifier = packageElem.getAttribute('unique-identifier') ?? undefined;

    metadata.author = getElemText(metadataElem, 'creator');
    metadata.description = getElemText(metadataElem, 'description');
    metadata.epub_version = packageElem.getAttribute('version')
      ? packageElem.getAttribute('version')
      : '';
    metadata.id = getElemText(metadataElem, 'identifier', uniqueIdentifier ? function(element) {
      return element.getAttribute('id') === uniqueIdentifier;
    } : undefined);
    metadata.language = getElemText(metadataElem, 'language');
    metadata.modified_date = getMetaElemPropertyText(metadataElem, 'dcterms:modified');
    metadata.ncx = spineElem.getAttribute('toc') ? spineElem.getAttribute('toc') : '';
    metadata.pubdate = getElemText(metadataElem, 'date');
    metadata.publisher = getElemText(metadataElem, 'publisher');
    metadata.rights = getElemText(metadataElem, 'rights');
    metadata.title = getElemText(metadataElem, 'title');

    metadata.page_prog_dir = spineElem.getAttribute('page-progression-direction');

    metadata.rendition_orientation = getMetaElemPropertyText(metadataElem, 'rendition:orientation');
    metadata.rendition_layout = getMetaElemPropertyText(metadataElem, 'rendition:layout');
    metadata.rendition_spread = getMetaElemPropertyText(metadataElem, 'rendition:spread');
    metadata.rendition_flow = getMetaElemPropertyText(metadataElem, 'rendition:flow');

    // Non-standard, used by Grumpy Monkey (9780553537888) to indicate the presence of a Javascript magnifier
    metadata.region_mag = getMetaElemContentByName(metadataElem, 'region-mag');

    //http://www.idpf.org/epub/301/spec/epub-publications.html#fxl-property-viewport

    //metadata.rendition_viewport = getMetaElemPropertyText(metadataElem, "rendition:viewport");
    metadata.rendition_viewport = getElemText(metadataElem, 'meta', function(element) {
      return (
        element.getAttribute('property') === 'rendition:viewport' &&
        !element.hasAttribute('refines')
      );
    });

    var viewports = [];
    var viewportMetaElems = filterXmlElemsByLocalNameAnyNS(metadataElem, 'meta', function(element) {
      return (
        element.getAttribute('property') === 'rendition:viewport' && element.hasAttribute('refines')
      );
    });
    _.each(viewportMetaElems, function(currItem) {
      var id = currItem.getAttribute('refines');
      if (id) {
        var hash = id.indexOf('#');
        if (hash >= 0) {
          var start = hash + 1;
          var end = id.length - 1;
          id = id.substr(start, end);
        }
        id = id.trim();
      }

      var vp = {
        refines: id,
        viewport: currItem.textContent,
      };
      viewports.push(vp);
    });

    metadata.rendition_viewports = viewports;

    // Media part
    metadata.mediaItems = [];

    var overlayElems = filterXmlElemsByLocalNameAnyNS(metadataElem, 'meta', function(element) {
      return (
        element.getAttribute('property') === 'media:duration' && element.hasAttribute('refines')
      );
    });

    _.each(overlayElems, function(currItem) {
      metadata.mediaItems.push({
        refines: currItem.getAttribute('refines'),
        duration: SmilDocumentParser.resolveClockValue(currItem.textContent),
      });
    });

    metadata.media_overlay = {
      duration: SmilDocumentParser.resolveClockValue(
        getElemText(metadataElem, 'meta', function(element) {
          return (
            element.getAttribute('property') === 'media:duration' &&
            !element.hasAttribute('refines')
          );
        }),
      ),
      narrator: getMetaElemPropertyText(metadataElem, 'media:narrator'),
      activeClass: getMetaElemPropertyText(metadataElem, 'media:active-class'),
      playbackActiveClass: getMetaElemPropertyText(metadataElem, 'media:playback-active-class'),
      smil_models: [],
      skippables: [
        'sidebar',
        'practice',
        'marginalia',
        'annotation',
        'help',
        'note',
        'footnote',
        'rearnote',
        'table',
        'table-row',
        'table-cell',
        'list',
        'list-item',
        'pagebreak',
      ],
      escapables: [
        'sidebar',
        'bibliography',
        'toc',
        'loi',
        'appendix',
        'landmarks',
        'lot',
        'index',
        'colophon',
        'epigraph',
        'conclusion',
        'afterword',
        'warning',
        'epilogue',
        'foreword',
        'introduction',
        'prologue',
        'preface',
        'preamble',
        'notice',
        'errata',
        'copyright-page',
        'acknowledgments',
        'other-credits',
        'titlepage',
        'imprimatur',
        'contributors',
        'halftitlepage',
        'dedication',
        'help',
        'annotation',
        'marginalia',
        'practice',
        'note',
        'footnote',
        'rearnote',
        'footnotes',
        'rearnotes',
        'bridgehead',
        'page-list',
        'table',
        'table-row',
        'table-cell',
        'list',
        'list-item',
        'glossary',
      ],
    };

    return new Metadata(metadata);
  }

  function getJsonManifest(xmlDom) {
    var $manifestItems = $(findXmlElemByLocalNameAnyNS(xmlDom, 'manifest')).children();
    var jsonManifest = [];

    $.each($manifestItems, function(manifestElementIndex, currManifestElement) {
      var $currManifestElement = $(currManifestElement);
      var currManifestElementHref = $currManifestElement.attr('href')
        ? $currManifestElement.attr('href')
        : '';
      var manifestItem = {
        href: currManifestElementHref,
        id: $currManifestElement.attr('id') ? $currManifestElement.attr('id') : '',
        media_overlay_id: $currManifestElement.attr('media-overlay')
          ? $currManifestElement.attr('media-overlay')
          : '',
        media_type: $currManifestElement.attr('media-type')
          ? $currManifestElement.attr('media-type')
          : '',
        properties: $currManifestElement.attr('properties')
          ? $currManifestElement.attr('properties')
          : '',
      };
      // console.log('pushing manifest item to JSON manifest. currManifestElementHref: [' + currManifestElementHref +
      //     '], manifestItem.href: [' + manifestItem.href +
      //     '], manifestItem:');
      // console.log(manifestItem);
      jsonManifest.push(manifestItem);
    });

    return jsonManifest;
  }

  function getCoverHref(xmlDom) {
    var manifest;
    var $imageNode;
    manifest = findXmlElemByLocalNameAnyNS(xmlDom, 'manifest');

    // epub3 spec for a cover image is like this:
    /*<item properties="cover-image" id="ci" href="cover.svg" media-type="image/svg+xml" />*/
    $imageNode = $(
      findXmlElemByLocalNameAnyNS(manifest, 'item', function(element) {
        var attr = element.getAttribute('properties');
        return attr && _.contains(attr.split(' '), 'cover-image');
      }),
    );
    if ($imageNode.length === 1 && $imageNode.attr('href')) {
      return $imageNode.attr('href');
    }

    // some epub2's cover image is like this:
    /*<meta name="cover" content="cover-image-item-id" />*/
    var metaNode = $(
      findXmlElemByLocalNameAnyNS(xmlDom, 'meta', function(element) {
        return element.getAttribute('name') === 'cover';
      }),
    );
    var contentAttr = metaNode.attr('content');
    if (metaNode.length === 1 && contentAttr) {
      $imageNode = $(
        findXmlElemByLocalNameAnyNS(manifest, 'item', function(element) {
          return element.getAttribute('id') === contentAttr;
        }),
      );
      if ($imageNode.length === 1 && $imageNode.attr('href')) {
        return $imageNode.attr('href');
      }
    }

    // that didn't seem to work so, it think epub2 just uses item with id=cover
    $imageNode = $(
      findXmlElemByLocalNameAnyNS(manifest, 'item', function(element) {
        return element.getAttribute('id') === 'cover';
      }),
    );
    if ($imageNode.length === 1 && $imageNode.attr('href')) {
      return $imageNode.attr('href');
    }

    // seems like there isn't one, thats ok...
    return null;
  }
};

export default PackageDocumentParser;
